import { getViewUrl, getViewUrlObject, isUrlExpired, type ViewUrl } from '@/helpers/documentHelpers';
import { useRefreshDocumentUrlsMutation } from '@/services/documents';
import { useCallback, useRef } from 'react';

import { ViewUrlType } from 'types';

interface RefreshedUrls {
  previewUrl?: string;
  originalUrl?: string;
  thumbnailUrl?: string;
  galleryUrl?: string;
}

interface UseDocumentUrlReturn {
  handleImageError: (
    event: React.SyntheticEvent<HTMLImageElement>,
    accountId: string,
    documentId: string,
    preferredType?: ViewUrlType,
  ) => Promise<void>;
  refreshAndGetUrl: (
    accountId: string,
    documentId: string,
    preferredType?: ViewUrlType,
  ) => Promise<string | undefined>;
  refreshAndGetAllUrls: (accountId: string, documentId: string) => Promise<RefreshedUrls | undefined>;
  getUrlWithAutoRefresh: (
    viewUrls: ViewUrl[] | undefined,
    accountId: string,
    documentId: string,
    preferredType?: ViewUrlType,
  ) => Promise<string | undefined>;
  isRefreshing: boolean;
}

/**
 * Hook for handling document URL expiration and refresh. Provides utilities
 * to refresh expired signed URLs on demand or in response to image-load
 * errors. Deduplicates refreshes per document/type combination to avoid
 * infinite loops on persistently broken URLs.
 */
export const useDocumentUrl = (): UseDocumentUrlReturn => {
  const [refreshUrls, { isLoading: isRefreshing }] = useRefreshDocumentUrlsMutation();
  // Tracks the most recent refresh attempt per `(account, document, type)` so
  // a single transient failure doesn't permanently blacklist the combination
  // for the lifetime of the component. The cooldown self-heals after
  // `REFRESH_COOLDOWN_MS`.
  const attemptedRefreshes = useRef<Map<string, number>>(new Map());

  const refreshAndGetUrl = useCallback(
    async (
      accountId: string,
      documentId: string,
      preferredType: ViewUrlType = ViewUrlType.original,
    ): Promise<string | undefined> => {
      try {
        const result = await refreshUrls({ accountId, documentId }).unwrap();
        const viewUrls = result.files?.[0]?.viewUrls as ViewUrl[] | undefined;
        return getViewUrl(viewUrls, preferredType);
      } catch (error) {
        console.error('Failed to refresh document URLs:', error);
        return undefined;
      }
    },
    [refreshUrls],
  );

  const refreshAndGetAllUrls = useCallback(
    async (accountId: string, documentId: string): Promise<RefreshedUrls | undefined> => {
      try {
        const result = await refreshUrls({ accountId, documentId }).unwrap();
        const viewUrls = result.files?.[0]?.viewUrls as ViewUrl[] | undefined;
        return {
          previewUrl: getViewUrl(viewUrls, ViewUrlType.preview),
          originalUrl: getViewUrl(viewUrls, ViewUrlType.original),
          thumbnailUrl: getViewUrl(viewUrls, ViewUrlType.thumbnail),
          galleryUrl: getViewUrl(viewUrls, ViewUrlType.gallery),
        };
      } catch (error) {
        console.error('Failed to refresh document URLs:', error);
        return undefined;
      }
    },
    [refreshUrls],
  );

  const handleImageError = useCallback(
    async (
      event: React.SyntheticEvent<HTMLImageElement>,
      accountId: string,
      documentId: string,
      preferredType: ViewUrlType = ViewUrlType.thumbnail,
    ): Promise<void> => {
      // React nulls out `currentTarget` once synchronous event dispatch ends, so
      // the element has to be captured before the await below.
      const image = event.currentTarget;
      const refreshKey = `${accountId}-${documentId}-${preferredType}`;
      const REFRESH_COOLDOWN_MS = 30_000;
      const lastAttempt = attemptedRefreshes.current.get(refreshKey);
      if (lastAttempt && Date.now() - lastAttempt < REFRESH_COOLDOWN_MS) {
        return;
      }
      attemptedRefreshes.current.set(refreshKey, Date.now());

      const newUrl = await refreshAndGetUrl(accountId, documentId, preferredType);
      if (newUrl) {
        image.src = newUrl;
      }
    },
    [refreshAndGetUrl],
  );

  const getUrlWithAutoRefresh = useCallback(
    async (
      viewUrls: ViewUrl[] | undefined,
      accountId: string,
      documentId: string,
      preferredType: ViewUrlType = ViewUrlType.original,
    ): Promise<string | undefined> => {
      const viewUrl = getViewUrlObject(viewUrls, preferredType);
      if (!viewUrl) return undefined;

      if (viewUrl.expiresAt && isUrlExpired(viewUrl.expiresAt, 60 * 1000)) {
        const newUrl = await refreshAndGetUrl(accountId, documentId, preferredType);
        return newUrl ?? viewUrl.url;
      }

      return viewUrl.url;
    },
    [refreshAndGetUrl],
  );

  return {
    handleImageError,
    refreshAndGetUrl,
    refreshAndGetAllUrls,
    getUrlWithAutoRefresh,
    isRefreshing,
  };
};
